import { formatCurrency, unlimitedMaxBenefitMagicNumber } from '@shared';
import { parseBioPercentToInflationRate } from '@client/util/policyQuoteCalculations';

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function formatBenefit(value?: number) {
  if (value === undefined || value === null) return '-';
  return value >= unlimitedMaxBenefitMagicNumber ? 'Unlimited' : formatCurrency(value);
}

export function formatInflatedMonthlyBenefit(
  inflatedMonthlyBenefit: number | undefined,
  inflatedMonthlyBenefitBase: number | undefined,
  inflatedMonthlyBenefitRider: number | undefined,
): string | undefined {
  if (
    isFiniteNumber(inflatedMonthlyBenefitBase) &&
    isFiniteNumber(inflatedMonthlyBenefitRider) &&
    inflatedMonthlyBenefitBase !== inflatedMonthlyBenefitRider
  ) {
    const baseFormatted = formatBenefit(inflatedMonthlyBenefitBase);
    const riderFormatted = formatBenefit(inflatedMonthlyBenefitRider);
    return `${baseFormatted} Base / ${riderFormatted} Rider`;
  }

  return formatBenefit(inflatedMonthlyBenefit);
}

/** 0.02, 2, "2", "2%" → 0.02 */
export function toNormalizedRate(inputRate: unknown): number | undefined {
  if (inputRate === null || inputRate === undefined) return undefined;

  if (typeof inputRate === 'string') {
    const trimmed = inputRate.trim();

    if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed === 'No BIO') return undefined;

    if (trimmed.includes('%')) {
      const viaBioRate = parseBioPercentToInflationRate(trimmed);

      if (Number.isFinite(viaBioRate)) return viaBioRate as number;
    }

    const numericValue = parseFloat(trimmed.replace('%', ''));

    if (!Number.isFinite(numericValue)) return undefined;

    return numericValue >= 1 ? numericValue / 100 : numericValue;
  }

  if (typeof inputRate === 'number' && Number.isFinite(inputRate)) {
    return inputRate >= 1 ? inputRate / 100 : inputRate;
  }

  return undefined;
}

export function formatPercentLabel(rateOrRaw?: number | unknown): string {
  const normalizedRate = toNormalizedRate(rateOrRaw);

  if (!isFiniteNumber(normalizedRate)) return 'N/A';

  const percent = normalizedRate * 100;

  return percent % 1 === 0 ? `${percent.toFixed(0)}%` : `${percent.toFixed(1)}%`;
}

export function formatYearsLabel(inputYears: number) {
  const roundedToTenth = Math.floor(inputYears * 10) / 10;

  return roundedToTenth % 1 === 0 ? String(Math.floor(roundedToTenth)) : String(roundedToTenth);
}
